import { useMemo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { getBusinessToday } from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Stack,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  LocationOn,
  Schedule,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  PlayArrow,
  AddShoppingCart,
  Search,
} from "@mui/icons-material";
import { deliveryService, Delivery, DeliveryStatus } from "@/services/delivery.service";
import { LoadingSpinner, PageHeader, EmptyState } from "@/components/ui";
import { LocalShipping } from "@mui/icons-material";
import { useToastStore } from "@/store/toastStore";
import { formatCustomerName } from "@/utils/formatCustomerName";

function arrayMove<T>(array: T[], fromIndex: number, toIndex: number): T[] {
  const copy = array.slice();
  const [removed] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, removed);
  return copy;
}

const STATUS_LABELS: Record<DeliveryStatus, string> = {
  PLANNED: "Planifiée",
  IN_PROGRESS: "En cours",
  DELIVERED: "Livrée",
  FAILED: "Échec",
};

function formatDateKey(d: Delivery): string {
  return d.deliveryDate ? format(new Date(d.deliveryDate), "yyyy-MM-dd") : "";
}

function deliveryStatusRank(status: DeliveryStatus): number {
  if (status === "DELIVERED" || status === "FAILED") return 1;
  return 0;
}

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

interface DeliveryCardProps {
  delivery: Delivery;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onView?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  isReordering: boolean;
}

function DeliveryCard({
  delivery,
  onMoveUp,
  onMoveDown,
  onView,
  canMoveUp,
  canMoveDown,
  isReordering,
}: DeliveryCardProps): JSX.Element {
  const addr = delivery.order?.customerAddress;
  const addressStr = addr
    ? [addr.address, addr.postalCode, addr.city].filter(Boolean).join(", ")
    : "—";
  const dateStr = delivery.deliveryDate
    ? format(new Date(delivery.deliveryDate), "EEE d MMM", { locale: fr })
    : "—";
  const timeStr =
    delivery.scheduledDeliveryTime ??
    delivery.order?.deliveryTimeSlot?.split("-")[0]?.trim() ??
    "—";

  return (
    <Paper sx={{ mb: 1.5, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "stretch", py: 1.5, px: 1.5 }}>
        <Stack
          direction="column"
          justifyContent="center"
          sx={{
            mr: 1,
            "& .MuiIconButton-root": { p: 0.5 },
          }}
        >
          <IconButton
            size="small"
            onClick={onMoveUp}
            disabled={!canMoveUp || isReordering}
            aria-label="Monter"
            sx={{ color: canMoveUp ? "text.secondary" : "action.disabled" }}
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onMoveDown}
            disabled={!canMoveDown || isReordering}
            aria-label="Descendre"
            sx={{ color: canMoveDown ? "text.secondary" : "action.disabled" }}
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {delivery.order?.orderNumber ?? "—"}
            </Typography>
            <Chip
              label={STATUS_LABELS[delivery.status] ?? delivery.status}
              size="small"
              color={
                delivery.status === "DELIVERED"
                  ? "success"
                  : delivery.status === "IN_PROGRESS"
                    ? "primary"
                    : "default"
              }
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
            <Schedule fontSize="small" sx={{ fontSize: 16 }} />
            <Typography variant="body2">
              {dateStr} · {timeStr}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700 }}>
            {delivery.order?.customer ? formatCustomerName(delivery.order.customer) : "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
            <LocationOn fontSize="small" sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary">
              {addressStr}
            </Typography>
          </Box>
          {onView && (
            <Button
              size="small"
              startIcon={<Visibility />}
              onClick={onView}
              sx={{ mt: 1 }}
            >
              Visualiser
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

/**
 * Liste des livraisons du livreur connecté avec réorganisation par boutons Monter / Descendre.
 * Groupées par date ; la réorganisation s’effectue au sein de chaque jour.
 */
export function DeliveriesMyPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToastStore();
  const [customerSearch, setCustomerSearch] = useState("");

  const today = getBusinessToday();
  const { data: deliveries, isLoading, error } = useQuery({
    queryKey: ["deliveries", "my", today],
    queryFn: () => deliveryService.getAll({ deliveryDate: today }),
  });

  const reorderMutation = useMutation({
    mutationFn: (updates: { deliveryId: string; tourOrder: number }[]) =>
      deliveryService.reorderMyDeliveries(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", "my"] });
      success("Ordre mis à jour");
    },
    onError: (err: Error) => {
      showError(err.message || "Erreur lors de la réorganisation");
    },
  });

  const confirmPreparedMutation = useMutation({
    mutationFn: (deliveryDate: string) => deliveryService.confirmPrepared(deliveryDate),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["deliveries", "my"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      success(
        data.updated > 0
          ? "Tournée démarrée : la première livraison est en cours."
          : "Aucune livraison planifiée à démarrer pour aujourd'hui."
      );
    },
    onError: (err: Error) => {
      showError(err.message || "Erreur lors de la confirmation");
    },
  });

  const byDate = useMemo(() => {
    const list = deliveries ?? [];
    const normalizedSearch = normalizeText(customerSearch);
    const filtered = normalizedSearch
      ? list.filter((d) =>
          normalizeText(d.order?.customer ? formatCustomerName(d.order.customer) : "").includes(normalizedSearch)
        )
      : list;
    const map = new Map<string, Delivery[]>();
    for (const d of filtered) {
      const key = formatDateKey(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(d);
    }
    for (const [, items] of map) {
      items.sort((a, b) => {
        const statusCompare = deliveryStatusRank(a.status) - deliveryStatusRank(b.status);
        if (statusCompare !== 0) return statusCompare;
        return a.tourOrder - b.tourOrder;
      });
    }
    const keys = Array.from(map.keys()).sort();
    return keys.map((key) => ({ dateKey: key, items: map.get(key)! }));
  }, [deliveries, customerSearch]);

  const handleMove = useCallback(
    (dateKey: string, fromIndex: number, direction: "up" | "down") => {
      const group = byDate.find((g) => g.dateKey === dateKey);
      if (!group) return;
      const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
      if (toIndex < 0 || toIndex >= group.items.length) return;
      const reordered = arrayMove(group.items, fromIndex, toIndex);
      const updates = reordered.map((d, i) => ({ deliveryId: d.id, tourOrder: i }));
      reorderMutation.mutate(updates);
    },
    [byDate, reorderMutation],
  );

  if (isLoading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center", minHeight: 200 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <PageHeader title="Mes livraisons" />
        <EmptyState
          icon={<LocalShipping />}
          title="Erreur"
          description="Impossible de charger vos livraisons."
        />
      </Box>
    );
  }

  const list = deliveries ?? [];
  const visibleCount = byDate.reduce((sum, group) => sum + group.items.length, 0);
  const isReordering = reorderMutation.isPending;

  const todayDeliveries = (list as Delivery[]).filter((d) => formatDateKey(d) === today);
  const canStartTour =
    todayDeliveries.some((d) => d.status === "PLANNED") &&
    !todayDeliveries.some((d) => d.status === "IN_PROGRESS");

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: "auto" }}>
      <PageHeader title="Mes livraisons" />
      {canStartTour && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<PlayArrow />}
          onClick={() => confirmPreparedMutation.mutate(today)}
          disabled={confirmPreparedMutation.isPending}
          sx={{ mb: 2 }}
        >
          {confirmPreparedMutation.isPending ? "Démarrage…" : "Démarrer ma tournée"}
        </Button>
      )}
      <Button
        variant="outlined"
        color="primary"
        fullWidth
        startIcon={<AddShoppingCart />}
        onClick={() => navigate("/orders/new")}
        sx={{ mb: 2 }}
      >
        Nouvelle commande
      </Button>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, mt: -1 }}>
        Passer une commande pour aujourd&apos;hui même après l&apos;heure limite (10 h).
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Rechercher un client..."
        value={customerSearch}
        onChange={(e) => setCustomerSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {visibleCount} livraison{visibleCount !== 1 ? "s" : ""} affichée
        {visibleCount !== 1 ? "s" : ""} sur {list.length} — utilisez les flèches pour réorganiser l’ordre
      </Typography>

      {visibleCount === 0 ? (
        <EmptyState
          icon={<LocalShipping />}
          title="Aucun résultat"
          description={
            customerSearch
              ? "Aucune livraison ne correspond à cette recherche client."
              : "Vous n'avez pas de livraison affectée."
          }
        />
      ) : (
        <>
          {byDate.map(({ dateKey, items }) => {
            const dateLabel = format(new Date(dateKey + "T12:00:00"), "EEEE d MMMM yyyy", {
              locale: fr,
            });
            return (
              <Box key={dateKey} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{ textTransform: "capitalize", mb: 1.5, fontWeight: 600 }}
                >
                  {dateLabel}
                </Typography>
                {items.map((d, index) => (
                  <DeliveryCard
                    key={d.id}
                    delivery={d}
                    canMoveUp={index > 0}
                    canMoveDown={index < items.length - 1}
                    isReordering={isReordering}
                    onMoveUp={index > 0 ? () => handleMove(dateKey, index, "up") : undefined}
                    onMoveDown={
                      index < items.length - 1 ? () => handleMove(dateKey, index, "down") : undefined
                    }
                    onView={() => navigate(`/deliveries/my/detail/${d.id}`)}
                  />
                ))}
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
}
